import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { IProjectLearner } from 'app/models/responses/IProjectLearner';
import React, { useRef, useState } from 'react';
import PublishIcon from '@material-ui/icons/Publish';
import axios from 'utils/axios';
import { rootConfig } from 'config';
import { useSnackbar } from 'notistack';
import buildQuery, { Expand } from 'odata-query';
import { ExceptionHandler } from 'components';
import getFileUrl from 'utils/getFileUrl';
import { IFileItem } from 'app/models/responses/IFileItem';
import { IEnrolmentFileType } from 'app/models/entities/IEnrolmentFileType';
import { useODataQuery } from 'hooks/useODataQuery';
import { IODataResponse } from 'app/models/responses/IODataResponse';

interface IProps {
  projectLearner: IProjectLearner;
  onSave: (projectLearner: IProjectLearner) => void;
}

const Files = ({ projectLearner, onSave }: IProps) => {
  const certificateInputRef = useRef<HTMLInputElement>(null);
  const resultsInputRef = useRef<HTMLInputElement>(null);
  const [certificate, setCertificate] = useState<File | null>(null);
  const [results, setResults] = useState<File | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const [isUploading, setUploading] = useState(false);
  const { data: expectedFiles } = useODataQuery<
    IODataResponse<IEnrolmentFileType[]>,
    IEnrolmentFileType
  >({
    resource: 'enrolmentFileType',
    query: {
      filter: {
        projectId: projectLearner.projectId,
      },
      expand: {
        fileType: {},
      },
    },
  });

  const handleAttachCertificate = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setCertificate(event.target.files[0]);
    }
  };

  const handleCertificateSelect = () => {
    if (certificateInputRef.current) certificateInputRef.current.click();
  };

  const handleAttachResults = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setResults(event.target.files[0]);
    }
  };

  const handleResultsSelect = () => {
    if (resultsInputRef.current) resultsInputRef.current.click();
  };

  const handleCertificateUpload = async () => {
    if (!certificate) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('projectLearnerId', projectLearner.id!.toString());
      formData.append('file', certificate);
      const expand: Expand<IProjectLearner> = {
        learner: {},
        mentor: {},
        project: {},
        certificate: {
          expand: {
            fileType: {},
          },
        },
        statementOfResults: {
          expand: {
            fileType: {},
          },
        },
      };
      const queryString = buildQuery({ expand });
      const response = await axios.post<IProjectLearner>(
        `${rootConfig.odataRoute}/projectLearners/uploadCertificate${queryString}`,
        formData,
      );
      onSave(response.data);
      enqueueSnackbar('Successfully uploaded certificate.', { variant: 'success' });
      setCertificate(null);
    } catch (error) {
      enqueueSnackbar(<ExceptionHandler exception={error} />, { variant: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const handleResultsUpload = async () => {
    if (!results) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('projectLearnerId', projectLearner.id!.toString());
      formData.append('file', results);
      const expand: Expand<IProjectLearner> = {
        learner: {},
        mentor: {},
        project: {},
        certificate: {
          expand: {
            fileType: {},
          },
        },
        statementOfResults: {
          expand: {
            fileType: {},
          },
        },
      };
      const queryString = buildQuery({ expand });
      const response = await axios.post<IProjectLearner>(
        `${rootConfig.odataRoute}/projectLearners/uploadResults${queryString}`,
        formData,
      );
      onSave(response.data);
      enqueueSnackbar('Successfully uploaded statement of results.', { variant: 'success' });
      setResults(null);
    } catch (error) {
      enqueueSnackbar(<ExceptionHandler exception={error} />, { variant: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const handleFileDownload = async (file: IFileItem | null | undefined) => {
    if (!file) return;

    try {
      saveAs(getFileUrl(file), file.displayName! + file.extension!);
    } catch (error) {
      enqueueSnackbar(<ExceptionHandler exception={error} />, { variant: 'error' });
    }
  };

  return (
    <Card>
      <CardHeader title='Files' />
      <Divider />
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Upload</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {expectedFiles?.value?.map((expectedFile) => (
              <TableRow key={expectedFile.id}>
                <TableCell>-</TableCell>
                <TableCell>{expectedFile.description}</TableCell>
                <TableCell>{expectedFile.fileType?.name}</TableCell>
                <TableCell>
                  <input
                    style={{ display: 'none' }}
                    ref={certificateInputRef}
                    type='file'
                    name='certificate'
                    onChange={handleAttachCertificate}
                    onClick={(event: any) => {
                      event.target.value = null;
                    }}
                  />
                  {certificate ? (
                    <>
                      <Button
                        color='primary'
                        variant='contained'
                        size='small'
                        startIcon={<PublishIcon />}
                        onClick={handleCertificateUpload}
                        disabled={isUploading}
                      >
                        Confirm
                      </Button>
                      <Typography variant='body2' color='textSecondary'>
                        {certificate.name}
                      </Typography>
                    </>
                  ) : (
                    <Button
                      onClick={handleCertificateSelect}
                      color='secondary'
                      variant='outlined'
                      size='small'
                    >
                      Browse Files
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {/* <TableRow>
              <TableCell>
                {projectLearner.certificate ? (
                  <Link
                    onClick={() => handleFileDownload(projectLearner.certificate)}
                    style={{ cursor: 'pointer' }}
                  >
                    {projectLearner.certificate?.displayName}
                    {projectLearner.certificate?.extension}
                  </Link>
                ) : (
                  '-'
                )}
              </TableCell>
              <TableCell>Certificate</TableCell>
              <TableCell>
                <input
                  style={{ display: 'none' }}
                  ref={certificateInputRef}
                  type='file'
                  name='certificate'
                  onChange={handleAttachCertificate}
                  onClick={(event: any) => {
                    event.target.value = null;
                  }}
                />
                {certificate ? (
                  <>
                    <Button
                      color='primary'
                      variant='contained'
                      size='small'
                      startIcon={<PublishIcon />}
                      onClick={handleCertificateUpload}
                      disabled={isUploading}
                    >
                      Confirm
                    </Button>
                    <Typography variant='body2' color='textSecondary'>
                      {certificate.name}
                    </Typography>
                  </>
                ) : (
                  <Button
                    onClick={handleCertificateSelect}
                    color='secondary'
                    variant='outlined'
                    size='small'
                  >
                    Browse Files
                  </Button>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                {projectLearner.statementOfResults ? (
                  <Link
                    onClick={() => handleFileDownload(projectLearner.statementOfResults)}
                    style={{ cursor: 'pointer' }}
                  >
                    {projectLearner.statementOfResults?.displayName}
                    {projectLearner.statementOfResults?.extension}
                  </Link>
                ) : (
                  '-'
                )}
              </TableCell>
              <TableCell>Statement of Results</TableCell>
              <TableCell>
                <input
                  style={{ display: 'none' }}
                  ref={resultsInputRef}
                  type='file'
                  name='statementOfResults'
                  onChange={handleAttachResults}
                  onClick={(event: any) => {
                    event.target.value = null;
                  }}
                />
                {results ? (
                  <>
                    <Button
                      color='primary'
                      variant='contained'
                      size='small'
                      startIcon={<PublishIcon />}
                      onClick={handleResultsUpload}
                      disabled={isUploading}
                    >
                      Confirm
                    </Button>
                    <Typography variant='body2' color='textSecondary'>
                      {results.name}
                    </Typography>
                  </>
                ) : (
                  <Button
                    onClick={handleResultsSelect}
                    color='secondary'
                    variant='outlined'
                    size='small'
                  >
                    Browse Files
                  </Button>
                )}
              </TableCell>
            </TableRow> */}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
};

export default Files;
